import type { ReactNode } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { PlannedOrder } from "@/api/planningRun";
import { cn } from "@/lib/utils";
import { formatDate, formatNumber } from "@/utils/formatters";
import StockChart from "@/views/items/detail/stock-chart/StockChart";

import { ExistingOrderTerms } from "./ExistingOrderTerms";
import { NodeOrders } from "./NodeOrders";
import { OrderActions } from "./OrderActions";
import { OrderSource } from "./OrderKind";
import { OrderStatus } from "./OrderStatus";
import { NeededFor, Needs } from "./Pegging";

export function OrderDetail({ order }: { order: PlannedOrder }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-6 p-4 lg:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="text-muted-foreground text-sm">
            <OrderSource order={order} />
          </div>
          <h2 className="text-lg font-semibold">
            <Link to={`/items/${order.item}?loc=${order.location}`}>
              {order.itemSku} · {order.itemName}
            </Link>
          </h2>
          <p className="text-muted-foreground text-sm">{order.locationName}</p>
        </div>
        <OrderActions order={order} />
      </header>

      <dl className="grid grid-cols-2 gap-4 rounded-md border p-4 text-sm sm:grid-cols-4">
        <Fact label={t("planningReview.quantity", "Quantity")}>
          <span
            className={cn(
              "text-lg font-semibold tabular-nums",
              order.status === "cancelled" && "line-through",
            )}
          >
            {formatNumber(Number(order.quantity), { maximumFractionDigits: 4 })}
          </span>
        </Fact>
        <Fact label={t("planningReview.acceptanceStatus", "Acceptance status")}>
          <OrderStatus status={order.status} />
        </Fact>
        {order.releaseDate && (
          <Fact label={t("planningReview.release", "Release")}>
            <span
              className={cn(
                "tabular-nums",
                order.releasePastDue && "text-destructive",
              )}
            >
              {formatDate(order.releaseDate)}
            </span>
            <p className="text-muted-foreground text-xs">
              {t("planningReview.leadTimeDays", "{{count}} days lead time", {
                count: order.leadTime,
              })}
            </p>
          </Fact>
        )}
        <Fact label={t("planningReview.arrival", "Arrival")}>
          <span className="tabular-nums">{formatDate(order.dueDate)}</span>
          {order.suggestedDueDate && (
            <p className="text-xs font-medium text-violet-700">
              {t(
                "planningReview.suggestedArrival",
                "Suggested arrival {{date}}",
                { date: formatDate(order.suggestedDueDate) },
              )}
            </p>
          )}
        </Fact>
        <Fact label={t("planningReview.source", "Source")} wide>
          {order.sourceCode} · {order.sourceName}
        </Fact>
        {order.timeFence && (
          <Fact
            label={t("planningReview.skipThrough", "Skip through (inclusive)")}
          >
            {formatDate(order.timeFence)}
          </Fact>
        )}
      </dl>

      <ExistingOrderTerms order={order} />
      {order.status !== "cancelled" && (
        <>
          <NeededFor key={order.id} run={order.run} orderId={order.id} />
          <Needs key={`needs-${order.id}`} run={order.run} orderId={order.id} />
        </>
      )}
      <StockChart
        key={`${order.run}-${order.item}-${order.location}`}
        itemId={order.item}
        locationId={order.location}
        runId={order.run}
        orderId={order.id}
      />
      <NodeOrders
        runId={order.run}
        itemId={order.item}
        locationId={order.location}
        selectedOrderId={order.id}
      />
    </section>
  );
}

function Fact({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn("min-w-0 wrap-anywhere", wide && "col-span-2")}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}
